import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Switch,
  FormControlLabel,
  IconButton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Paper,
  Slide,
  Link,
} from '@mui/material';
import {
  Close as CloseIcon,
  ExpandMore as ExpandMoreIcon,
  Cookie as CookieIcon,
  Security as SecurityIcon,
  Analytics as AnalyticsIcon,
  Campaign as CampaignIcon,
  Settings as SettingsIcon,
} from '@mui/icons-material';
import { TransitionProps } from '@mui/material/transitions';
import { styled } from '@mui/material/styles';

const Transition = React.forwardRef(function Transition(
  props: TransitionProps & {
    children: React.ReactElement<any, any>;
  },
  ref: React.Ref<unknown>,
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const CookieBanner = styled(Paper)(({ theme }) => ({
  position: 'fixed',
  bottom: 0,
  left: 0,
  right: 0,
  padding: theme.spacing(3),
  background: 'linear-gradient(135deg, #0C1319 0%, #1a2332 100%)',
  borderTop: '2px solid #4DC4D2',
  zIndex: 1300,
  [theme.breakpoints.up('md')]: {
    bottom: theme.spacing(2),
    left: theme.spacing(2),
    right: 'auto',
    maxWidth: '450px',
    borderRadius: theme.shape.borderRadius,
    border: '2px solid #4DC4D2',
  },
}));

interface CookiePreferences {
  essential: boolean;
  analytics: boolean;
  marketing: boolean;
  functional: boolean;
}

interface CookieConsentProps {
  onAccept?: (preferences: CookiePreferences) => void;
  onDecline?: () => void;
}

const CookieConsent: React.FC<CookieConsentProps> = ({ onAccept, onDecline }) => {
  const [showBanner, setShowBanner] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [preferences, setPreferences] = useState<CookiePreferences>({
    essential: true, // Always true, cannot be changed
    analytics: false,
    marketing: false,
    functional: true,
  });

  useEffect(() => {
    // Check if user has already made a cookie choice
    const cookieConsent = localStorage.getItem('cookieConsent');
    const shouldShowBanner = localStorage.getItem('showCookieBanner');

    if (!cookieConsent || shouldShowBanner === 'true') {
      // Show banner after a short delay for better UX
      setTimeout(() => setShowBanner(true), 1000);
      localStorage.removeItem('showCookieBanner');
    }

    // Listen for events to show cookie settings
    const handleShowSettings = () => {
      setShowBanner(true);
      setShowDetails(true);
    };

    window.addEventListener('showCookieSettings', handleShowSettings);

    return () => {
      window.removeEventListener('showCookieSettings', handleShowSettings);
    };
  }, []);

  const handleAcceptAll = () => {
    const allAccepted: CookiePreferences = {
      essential: true,
      analytics: true,
      marketing: true,
      functional: true,
    };

    localStorage.setItem('cookieConsent', JSON.stringify({
      preferences: allAccepted,
      timestamp: new Date().toISOString(),
      version: '1.0.0'
    }));

    setShowBanner(false);
    setShowDetails(false);
    onAccept?.(allAccepted);
  };

  const handleAcceptSelected = () => {
    localStorage.setItem('cookieConsent', JSON.stringify({
      preferences,
      timestamp: new Date().toISOString(),
      version: '1.0.0'
    }));

    setShowBanner(false);
    setShowDetails(false);
    onAccept?.(preferences);
  };

  const handleDeclineAll = () => {
    const essentialOnly: CookiePreferences = {
      essential: true,
      analytics: false,
      marketing: false,
      functional: false,
    };

    localStorage.setItem('cookieConsent', JSON.stringify({
      preferences: essentialOnly,
      timestamp: new Date().toISOString(),
      version: '1.0.0'
    }));

    setShowBanner(false);
    setShowDetails(false);
    onDecline?.();
  };

  const handlePreferenceChange = (category: keyof CookiePreferences) => {
    if (category === 'essential') return; // Essential cookies cannot be disabled

    setPreferences(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
  };

  const cookieCategories = [
    {
      id: 'essential',
      name: 'Essential Cookies',
      icon: <SecurityIcon />,
      required: true,
      description: 'These cookies are necessary for the website to function and cannot be switched off. They are usually only set in response to actions made by you such as logging in or filling in forms.',
      cookies: [
        { name: 'authToken', purpose: 'User authentication', duration: 'Session' },
        { name: 'sessionId', purpose: 'Session management', duration: '24 hours' },
        { name: 'csrfToken', purpose: 'Security token', duration: 'Session' },
      ]
    },
    {
      id: 'functional',
      name: 'Functional Cookies',
      icon: <SettingsIcon />,
      required: false,
      description: 'These cookies enable the website to provide enhanced functionality and personalization. They may be set by us or by third party providers whose services we have added to our pages.',
      cookies: [
        { name: 'userPreferences', purpose: 'User interface settings', duration: '1 year' },
        { name: 'language', purpose: 'Language preference', duration: '1 year' },
        { name: 'theme', purpose: 'Theme preference', duration: '1 year' },
      ]
    },
    {
      id: 'analytics',
      name: 'Analytics Cookies',
      icon: <AnalyticsIcon />,
      required: false,
      description: 'These cookies allow us to count visits and traffic sources so we can measure and improve the performance of our site. They help us understand which pages are the most and least popular.',
      cookies: [
        { name: '_ga', purpose: 'Google Analytics', duration: '2 years' },
        { name: '_gid', purpose: 'Google Analytics', duration: '24 hours' },
        { name: 'amplitude', purpose: 'Product analytics', duration: '1 year' },
      ]
    },
    {
      id: 'marketing',
      name: 'Marketing Cookies',
      icon: <CampaignIcon />,
      required: false,
      description: 'These cookies may be set through our site by our advertising partners. They may be used by those companies to build a profile of your interests and show you relevant adverts on other sites.',
      cookies: [
        { name: 'fbp', purpose: 'Facebook Pixel', duration: '90 days' },
        { name: 'gcl_au', purpose: 'Google Ads', duration: '90 days' },
        { name: 'li_sugr', purpose: 'LinkedIn Insight', duration: '90 days' },
      ]
    },
  ];

  if (!showBanner) return null;

  return (
    <>
      {/* Simple Cookie Banner */}
      {!showDetails && (
        <CookieBanner elevation={8}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 16 }}>
            <CookieIcon style={{ color: '#4DC4D2', marginTop: 4 }} />
            <div style={{ flex: 1 }}>
              <Typography variant="h6" style={{ color: 'white', marginBottom: 8 }}>
                Cookie Preferences
              </Typography>
              <Typography variant="body2" style={{ color: 'rgba(255, 255, 255, 0.8)', marginBottom: 16 }}>
                We use cookies to enhance your experience, analyze site traffic, and personalize content.
                By clicking "Accept All", you consent to our use of cookies. You can also customize your preferences.
              </Typography>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <Button
                  variant="contained"
                  size="small"
                  onClick={handleAcceptAll}
                  style={{
                    backgroundColor: '#4DC4D2',
                  }}
                >
                  Accept All
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={handleDeclineAll}
                  style={{
                    color: 'white',
                    borderColor: 'rgba(255, 255, 255, 0.3)',
                  }}
                >
                  Reject All
                </Button>
                <Button
                  variant="text"
                  size="small"
                  onClick={() => setShowDetails(true)}
                  style={{
                    color: '#4DC4D2',
                  }}
                >
                  Customize
                </Button>
              </div>
              <Typography variant="caption" style={{ color: 'rgba(255, 255, 255, 0.6)', display: 'block', marginTop: 8 }}>
                Read our{' '}
                <Link
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    // Open privacy policy - you can link this to your TermsCheckbox dialog
                  }}
                  style={{ color: '#4DC4D2' }}
                >
                  Privacy Policy
                </Link>{' '}
                for more information.
              </Typography>
            </div>
          </div>
        </CookieBanner>
      )}

      {/* Detailed Cookie Settings Dialog */}
      <Dialog
        open={showDetails}
        onClose={() => setShowDetails(false)}
        TransitionComponent={Transition}
        maxWidth="md"
        fullWidth
        PaperProps={{
          style: {
            background: 'linear-gradient(135deg, #0C1319 0%, #1a2332 100%)',
            color: 'white',
          }
        }}
      >
        <DialogTitle style={{ margin: 0, padding: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
          <CookieIcon style={{ color: '#4DC4D2' }} />
          <Typography variant="h6" component="span">
            Cookie Settings
          </Typography>
          <IconButton
            aria-label="close"
            onClick={() => setShowDetails(false)}
            style={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: 'rgba(255, 255, 255, 0.5)',
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers style={{ borderColor: 'rgba(255, 255, 255, 0.1)' }}>
          <Typography variant="body2" style={{ marginBottom: 24, color: 'rgba(255, 255, 255, 0.8)' }}>
            When you visit our website, we may store or retrieve information on your browser, mostly in the form of cookies.
            This information might be about you, your preferences, or your device and is mostly used to make the site work as you expect it to.
          </Typography>

          {cookieCategories.map((category) => (
            <Accordion
              key={category.id}
              style={{
                backgroundColor: 'rgba(255, 255, 255, 0.05)',
                color: 'white',
                marginBottom: 8,
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon style={{ color: 'rgba(255, 255, 255, 0.5)' }} />}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 16, flex: 1 }}>
                  <div style={{ color: '#4DC4D2' }}>{category.icon}</div>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <Typography>{category.name}</Typography>
                      {category.required && (
                        <Chip
                          label="Always Active"
                          size="small"
                          style={{
                            backgroundColor: '#4DC4D2',
                            color: 'white',
                            height: '20px',
                          }}
                        />
                      )}
                    </div>
                  </div>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={preferences[category.id as keyof CookiePreferences]}
                        onChange={() => handlePreferenceChange(category.id as keyof CookiePreferences)}
                        disabled={category.required}
                      />
                    }
                    label=""
                    onClick={(e) => e.stopPropagation()}
                  />
                </div>
              </AccordionSummary>
              <AccordionDetails>
                <Typography variant="body2" style={{ marginBottom: 16, color: 'rgba(255, 255, 255, 0.7)' }}>
                  {category.description}
                </Typography>
                {category.cookies.length > 0 && (
                  <div>
                    <Typography variant="subtitle2" style={{ marginBottom: 8, color: '#4DC4D2' }}>
                      Cookies in this category:
                    </Typography>
                    <div style={{ paddingLeft: 16 }}>
                      {category.cookies.map((cookie, index) => (
                        <div key={index} style={{ marginBottom: 8 }}>
                          <Typography variant="body2" style={{ fontWeight: 'medium' }}>
                            {cookie.name}
                          </Typography>
                          <Typography variant="caption" style={{ color: 'rgba(255, 255, 255, 0.6)' }}>
                            Purpose: {cookie.purpose} | Duration: {cookie.duration}
                          </Typography>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </AccordionDetails>
            </Accordion>
          ))}
        </DialogContent>

        <DialogActions style={{ padding: 16, borderTop: '1px solid rgba(255, 255, 255, 0.1)' }}>
          <Button
            onClick={handleDeclineAll}
            style={{
              color: 'rgba(255, 255, 255, 0.7)',
            }}
          >
            Reject All
          </Button>
          <Button
            onClick={handleAcceptSelected}
            variant="outlined"
            style={{
              color: 'white',
              borderColor: 'rgba(255, 255, 255, 0.3)',
            }}
          >
            Save Preferences
          </Button>
          <Button
            onClick={handleAcceptAll}
            variant="contained"
            style={{
              backgroundColor: '#4DC4D2',
            }}
          >
            Accept All
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default CookieConsent;
